import { SectionIds } from '@modeso/aumico-lib-annualreport-fe';
import { Translation } from '@modeso/aumico-lib-core-fe';
import { IAnnualReport, IDisplayOptions, IFinStatements, ITranslationEntry } from '@modeso/types__aumico-lib-annualreport-be';
import { IReportSectionsTitles } from '@modeso/types__aumico-lib-annualreport-be/dist/models/interfaces/report-sections-titles.interface';
import { INotesTypeEntityDTO } from '@modeso/types__aumico-lib-notes-be';
import { I18NextService } from 'angular-i18next';
import Debug from 'debug';
import { cloneDeep } from 'lodash-es';
import { templateConstants } from '../../../../shared/template-constants';
import { TranslationJobHelper } from '../../helpers/translationJob.helper';
import { AppropriationOfProfitTranslatorVisitor } from './AppropriationOfProfitTranslatorVisitor';
import { CashFlowTranslatorVisitor } from './CashFlowTranslatorVisitor';
import { ComplementaryReportsTranslatorVisitor } from './ComplementaryReportsTranslatorVisitor';
import { EmsTranslatorVisitor } from './EmsTranslatorVisitor';
import { TranslationMarks } from './GapReportingVisitor';
import { IAnnualReportSectionsVisitor } from './IAnnualReportVisitor.interface';
import { NotesTranslatorVisitor } from './NotesTranslatorVisitor';
import { TemplateTranslatorVisitor } from './TemplateTranslatorVisitor';
import { UntranslatedTexts } from './UntranslatedTexts';
import { AppropriationOfProfit } from './classes/AppropriationOfProfit';
import { CashFlow } from './classes/CashFlow';
import { ComplementaryReports } from './classes/ComplementaryReports';
import { Ems } from './classes/Ems';
import { Notes } from './classes/Notes';
import { Template } from './classes/Template';

const debugLang = Debug('aumico:AnnualReportVisitor//language');
const predifinedLablesKeys = [
    'aumico_reports_annualreport_cover_annual_label_title_1st_prefix',
    'aumico_reports_annualreport_cover_label_title_2nd_prefix',
    'aumico_reports_annualreport_cover_quarterly_label_title_1st_prefix',
    'aumico_reports_annualreport_cover_monthly_label_title_1st_prefix',
    'aumico_reports_annualreport_cover_custom_label_title_1st_prefix',
    'aumico_reports_annualreport_page_print_account_mapping_label_total',
    'aumico_reports_annualreport_page_print_account_mapping_label_word_before_currency',
    'aumico_reports_annualreport_page_print_account_mapping_label_notes',
    'aumico_annual_report_cash_flow_total_recapitulation',
    'aumico_annual_report_cash_flow_total_cashAndCashStart',
    'aumico_annual_report_cash_flow_total_cashAndCashEnd',
    'aumico_annual_report_ems_row_title',
    'aumico_annual_report_ems_business_year',
    'aumico_annual_report_ems_total_equity_header_text',
    'aumico_reports_annualreport_toc_label_table_of_contents',
    'aumico_annual_report_profit_calculation_annual_profit',
    'aumico_report_fas_print_section_costs_title',
    'aumico_report_fas_print_costs_additions',
    'aumico_report_fas_print_costs_disposals',
    'aumico_report_fas_print_costs_transfers',
    'aumico_report_fas_print_section_costs_title',
    'aumico_report_fas_print_section_depr_title',
    'aumico_report_fas_print_depreciations_additions',
    'aumico_report_fas_print_depreciations_disposals',
    'aumico_annual_report_print_preview_fcp',
    'aumico_report_fas_print_depreciations_transfers',
    'aumico_report_fas_print_section_valueApr_title',
    'aumico_report_fas_print_section_bookvalue_current',
    'aumico_report_fas_print_section_bookvalue_previous',
    'aumico_report_fas_print_subtotal',
    'aumico_annual_report_account_proof_display_account_number_column_header',
    'aumico_annual_report_account_proof_display_account_name_column_header',
    'aumico_annual_report_display_current_date_column_header',
    'aumico_annual_report_display_previous_date_column_header',
    'aumico_annual_report_fas_direct_assigned_accounts',
    'aumico_reports_annualreport_print_label_previous_year',
    'aumico_reports_annualreport_print_label_current_year',
    'aumico_reports_annualreport_print_label_ratio',
    'aumico_reports_annualreport_print_label_taxdomicile',
    'aumico_reports_annualreport_print_label_taxid',
    'aumico_reports_annualreport_print_label_assets',
    'aumico_reports_annualreport_print_label_liabilities',
    'aumico_reports_annualreport_print_label_page',
    'aumico_reports_print_title_establishment',
    'aumico_reports_annualreport_print_date_from',
    'aumico_reports_annualreport_print_date_to',
    'aumico_reports_annualreport_print_date_of',
    'aumico_cover_page_internal_label',
    'aumico_annual_report_print_page_rounding_disclaimer',
    'aumico_annual_report_fx_notes_reserve_label',
    'aumico_cover_page_draft_label',
];

const predefinedSectionsTitles = [
    'aumico_annual_report_print_preview_balance',
    'aumico_annual_report_print_preview_income',
    'aumico_annual_report_print_preview_attachement',
    'aumico_annual_report_print_preview_aop',
    'aumico_annual_report_print_preview_cashflow',
    'aumico_annual_report_print_preview_ems',
    'aumico_annual_report_print_preview_fas',
    'aumico_annual_report_print_preview_account_proof_for_income',
    'aumico_annual_report_print_preview_account_proof_for_balance',
    'aumico_annual_report_print_preview_appendix_section_title',
];

export class AnnualReportVisitor implements IAnnualReportSectionsVisitor {
    private _translatedReport: IAnnualReport;
    private _translatedFinStatments: IFinStatements[];
    private _predefinedLables: Map<string, string>;
    private reportWideNamesDone = false;
    private _translationEntries: Record<string, ITranslationEntry> | undefined;
    private readonly _translationMarks: TranslationMarks = new Map();

    get translatedReport(): IAnnualReport {
        return this._translatedReport;
    }

    get predifinedLabels(): Map<string, string> {
        return this._predefinedLables;
    }
    get translatedFinStatments(): IFinStatements[] {
        return this._translatedFinStatments;
    }
    /** Every text `resolve` inserted in this run, with its origin — what the review marks. */
    get translationMarks(): TranslationMarks {
        return this._translationMarks;
    }
    constructor(
        private readonly targetLanguage: string,
        private readonly reportLanguage: string,
        private readonly translatableItems: Map<string, Translation>,
        private readonly reportSectionsTitles: IReportSectionsTitles,
        private readonly i18NextService: I18NextService,
        private readonly untranslated?: UntranslatedTexts,
        private readonly displayOptions?: IDisplayOptions,
        private readonly notesTypes?: INotesTypeEntityDTO[],
    ) {
        this._predefinedLables = new Map<string, string>();
        this.setPredefinedLabels();
    }

    private setPredefinedLabels() {
        debugLang('setPredefinedLabels', this.targetLanguage, this.reportLanguage);
        for (const predifinedLableKey of predifinedLablesKeys) {
            this._predefinedLables.set(
                predifinedLableKey,
                this.i18NextService.t(predifinedLableKey, { lng: this.targetLanguage }) as string,
            );
        }

        for (const predefinedSectionTitle of predefinedSectionsTitles) {
            let sectionId: string | undefined;
            switch (predefinedSectionTitle) {
                case 'aumico_annual_report_print_preview_balance':
                    sectionId = SectionIds.BALANCE;
                    break;
                case 'aumico_annual_report_print_preview_income':
                    sectionId = SectionIds.INCOME;
                    break;
                case 'aumico_annual_report_print_preview_aop':
                    sectionId = SectionIds.AOP;
                    break;
                case 'aumico_annual_report_print_preview_account_proof_for_balance':
                    sectionId = SectionIds.APBS;
                    break;
                case 'aumico_annual_report_print_preview_account_proof_for_income':
                    sectionId = SectionIds.APIS;
                    break;
                case 'aumico_annual_report_print_preview_cashflow':
                    sectionId = SectionIds.CASHFLOW;
                    break;
                case 'aumico_annual_report_print_preview_attachement':
                    sectionId = SectionIds.NOTES;
                    break;
                case 'aumico_annual_report_print_preview_ems':
                    sectionId = SectionIds.EMS;
                    break;
                case 'aumico_annual_report_print_preview_fas':
                    sectionId = SectionIds.FAS;
                    break;
                case 'aumico_annual_report_print_preview_appendix_section_title':
                    sectionId = SectionIds.APPENDIX;
                    break;
                default:
                    break;
            }
            if (this.reportSectionsTitles != null) {
                this._predefinedLables.set(
                    predefinedSectionTitle,
                    this.reportSectionsTitles[sectionId] != null &&
                        this.reportSectionsTitles[sectionId][this.targetLanguage] != null
                        ? this.reportSectionsTitles[sectionId][this.targetLanguage]
                        : (this.i18NextService.t(predefinedSectionTitle, { lng: this.targetLanguage }) as string),
                );
            } else {
                this._predefinedLables.set(
                    predefinedSectionTitle,
                    this.i18NextService.t(predefinedSectionTitle, { lng: this.targetLanguage }) as string,
                );
            }
        }
    }

    visit(annualReport: IAnnualReport, finStatements: IFinStatements[]): void {
        debugLang('AnnualReportVisitor.visit', annualReport, finStatements);
        this._translatedReport = cloneDeep<IAnnualReport>(annualReport);
        this._translatedFinStatments = cloneDeep<IAnnualReport>(finStatements);

        const balanceSheetGroup =
            this._translatedFinStatments[templateConstants.balanceTemplate].templateAndAccountsMapping.template.group;

        const balanceSheetTemplate = new Template(balanceSheetGroup);
        this.visitTemplate(balanceSheetTemplate, templateConstants.balanceTemplate);

        const incomeSheetGroup =
            this._translatedFinStatments[templateConstants.incomeTemplate].templateAndAccountsMapping.template.group;
        const incomeSheetTemplate = new Template(incomeSheetGroup);
        this.visitTemplate(incomeSheetTemplate, templateConstants.incomeTemplate);

        if (this._translatedReport.cashFlowStatement != null) {
            const cashFlow = new CashFlow(this._translatedReport.cashFlowStatement);
            this.visitCashFlow(cashFlow);
        }

        if (this._translatedReport.appropriationOfProfit != null) {
            const aop = new AppropriationOfProfit(this._translatedReport.appropriationOfProfit);
            this.visitAop(aop);
        }

        if (this._translatedReport.notes != null) {
            const notes = new Notes(this._translatedReport.notes);
            this.visitNotes(notes);
        }

        if (this._translatedReport.equityMovementSchedule != null) {
            const ems = new Ems(this._translatedReport.equityMovementSchedule);
            this.visitEms(ems);
        }

        if (this._translatedReport.complementaryReports != null) {
            const complementaryReports = new ComplementaryReports(this._translatedReport.complementaryReports);
            this.visitComplementaryReports(complementaryReports);
        }
    }

    visitTemplate(template: Template, templateIndex: number) {
        // Renames and group names belong to the report, not to a statement: only the first of the
        // two template runs may touch them, otherwise the second translates the first one's output.
        const templateTranslator = new TemplateTranslatorVisitor(
            this.targetLanguage,
            this.translatableItems,
            this.reportLanguage,
            this.untranslated,
            this._translatedReport?.groupingAndMasking,
            this.translationEntries,
            !this.reportWideNamesDone,
        );
        this.reportWideNamesDone = true;
        templateTranslator.recordMarksInto(this._translationMarks);
        template.accept(templateTranslator);
        this._translatedFinStatments[templateIndex].templateAndAccountsMapping.template.group = template.group;
    }

    visitNotes(notes: Notes): void {
        const notesTranslator = new NotesTranslatorVisitor(
            this.targetLanguage,
            this.translatableItems,
            this.reportLanguage,
            this.untranslated,
            this.notesTypes,
            this.translationEntries,
        );
        notesTranslator.recordMarksInto(this._translationMarks);
        notes.accept(notesTranslator);
    }

    visitAop(appropriationOfProfit: AppropriationOfProfit): void {
        const aopTranslator = new AppropriationOfProfitTranslatorVisitor(
            this.targetLanguage,
            this.reportLanguage,
            this.i18NextService,
            this.untranslated,
            this.translationEntries,
        );
        aopTranslator.recordMarksInto(this._translationMarks);
        appropriationOfProfit.accept(aopTranslator);
    }

    visitCashFlow(cashFlow: CashFlow): void {
        const cashFlowTranslator = new CashFlowTranslatorVisitor(
            this.targetLanguage,
            this.reportLanguage,
            this.translatableItems,
            this.i18NextService,
            this.untranslated,
            this.translationEntries,
        );
        cashFlowTranslator.recordMarksInto(this._translationMarks);
        cashFlow.accept(cashFlowTranslator);
    }

    visitEms(ems: Ems): void {
        const emsTranslator = new EmsTranslatorVisitor(
            this.reportLanguage,
            this.targetLanguage,
            this.i18NextService,
            this._translatedFinStatments?.[templateConstants.balanceTemplate]?.templateAndAccountsMapping?.template,
            this.translatableItems,
            this.untranslated,
            this.translationEntries,
        );
        emsTranslator.recordMarksInto(this._translationMarks);
        ems.accept(emsTranslator);
    }

    visitComplementaryReports(reports: ComplementaryReports): void {
        const complementaryReportsTranslator = new ComplementaryReportsTranslatorVisitor(
            this.untranslated,
            this.translationEntries,
        );
        complementaryReportsTranslator.recordMarksInto(this._translationMarks);
        reports.accept(complementaryReportsTranslator);
    }

    /** The saved translation entries for the target language, source text → entry, with the mapping's
     * translation of the same text ahead of the AI. Built once per visitor run. */
    private get translationEntries(): Record<string, ITranslationEntry> | undefined {
        if (this._translationEntries == null) {
            this._translationEntries = TranslationJobHelper.withMasterData(
                this._translatedReport?.translations?.[this.targetLanguage]?.entries,
                this.translatableItems,
                this.reportLanguage,
                this.targetLanguage,
            );
        }

        return this._translationEntries;
    }
}
